import { medicalDocUsage, reservationFieldUsage } from "./constant/classification"
import { ElapsedMillisecond, toApiYmd } from '../../utils/dateUtil';
import { executeGetProjectDetail } from '../../dataAccess/webApi/dao/projectsDao';
import { GetProjectDetailDto } from '../../dataAccess/webApi/dto/projectsDto';
import { getFacility } from './facility';
import { ReservationDetailSettingDto } from "../../dataAccess/webApi/dto/reservationsDto";

/**
 * 複数日程に対して参照する事業マスタ情報を取得する
 * 24/11 HOIKU-99にて予約希望日ないで最も古い日付における事業マスタを参照することに決定
 */
export const getProjectMstDataToRefer = async (facilityId: string, usageDates: Date[] | ElapsedMillisecond[], projectId?: string): Promise<GetProjectDetailDto> => {
    // 日付を古い順にソート
    usageDates.sort((a, b) => new Date(a).getTime() - new Date(b).getTime());

    if (!projectId) {
        const facilityData = await getFacility(facilityId, usageDates[0]);
        projectId = facilityData.projectId;
    }
    
    const getProjectDetailReturn = await executeGetProjectDetail(projectId, toApiYmd(usageDates[0]));
    return getProjectDetailReturn.result;
}

// 予約時利用項目
export type FieldsType<T> = {
    diagonosis: T,
    symptom: T,
    epidemic: T,
    medicine: T,
    bodyTemp: T,
    foodAllergy: T,
    febrileSeizures: T,
    medicalDoc: T,
    emergency: T,
    height: T,
    weight: T,
    eatingMethod: T,
    eatingAbility: T,
    action: T,
    toiletAbility: T,
    considerations: T,
    reduction: T,
    attachedDoc: T,
}

export type FieldRule = {
    show: boolean
    required: boolean
    order: number | null
    mustHospitalInfo?: boolean
}

// 予約時利用項目表示有無情報カラム
const fieldShowRuleMap: FieldsType<string> = {
    diagonosis: "diagonosisInput",
    symptom: "symptomsInput",
    epidemic: "epidemicInput",
    medicine: "medicineInput",
    bodyTemp: "bodyTemperatureInput",
    febrileSeizures: "febrileSeizuresInput",
    medicalDoc: "medicalDocUsage",
    emergency: "emergencyActionInput",
    height: "heightInput",
    weight: "weightInput",
    eatingMethod: "eatingMethodInput",
    foodAllergy: "allergyInput",
    eatingAbility: "eatingAbilityInput",
    action: "actionInput",
    toiletAbility: "toiletAbilityInput",
    considerations: "considerationsInput",
    reduction: "reductionInput",
    attachedDoc: "attachedDocInput",
}

// 予約時利用項目並び順情報カラム
const fieldOrderRuleMap: FieldsType<string> = {
    diagonosis: "diagonosisOrder",
    symptom: "symptomsOrder",
    epidemic: "epidemicOrder",
    medicine: "medicineOrder",
    bodyTemp: "bodyTemperatureOrder",
    febrileSeizures: "febrileSeizuresOrder",
    medicalDoc: "medicalDocOrder",
    emergency: "emergencyActionOrder",
    height: "heightOrder",
    weight: "weightOrder",
    eatingMethod: "eatingMethodOrder",
    foodAllergy: "allergyOrder",
    eatingAbility: "eatingAbilityOrder",
    action: "actionOrder",
    toiletAbility: "toiletAbilityOrder",
    considerations: "considerationsOrder",
    reduction: "reductionOrder",
    attachedDoc: "attachedDocOrder",
}

export const getByChildFormFieldRule = (projectMst: GetProjectDetailDto, isolationFlag?: Boolean, useReseravtionSetting?: ReservationDetailSettingDto[] | null): FieldsType<FieldRule> => {
    // 並び順未設定の場合はこの順番で表示される
    const rule: FieldsType<FieldRule> = {
        diagonosis: { show: false, required: false, order: null },
        symptom: { show: false, required: false, order: null },
        epidemic: { show: false, required: false, order: null },
        medicine: { show: false, required: false, order: null },
        bodyTemp: { show: false, required: false, order: null },
        foodAllergy: { show: false, required: false, order: null },
        febrileSeizures: { show: false, required: false, order: null },
        medicalDoc: { show: false, required: false, order: null, mustHospitalInfo: false },
        emergency: { show: false, required: false, order: null },
        height: { show: false, required: false, order: null },
        weight: { show: false, required: false, order: null },
        eatingMethod: { show: false, required: false, order: null },
        eatingAbility: { show: false, required: false, order: null },
        action: { show: false, required: false, order: null },
        toiletAbility: { show: false, required: false, order: null },
        considerations: { show: false, required: false, order: null },
        reduction: { show: false, required: false, order: null },
        attachedDoc: { show: false, required: false, order: null },
    };

    Object.keys(fieldShowRuleMap).forEach((key) => {
        const fieldKey = key as keyof FieldsType<string>;
        const ruleKeyName = fieldShowRuleMap[fieldKey] as keyof GetProjectDetailDto;

        if (ruleKeyName === fieldShowRuleMap.medicalDoc) {
            const fieldFlag = (projectMst[ruleKeyName] === medicalDocUsage.byFacility && useReseravtionSetting !== null && useReseravtionSetting !== undefined) ? useReseravtionSetting[0].medicalDoc : projectMst[ruleKeyName]
            if (fieldFlag !== medicalDocUsage.noUse) {
                rule.medicalDoc.show = true
                if (fieldFlag === medicalDocUsage.mustHospitalInfo) {
                    rule.medicalDoc.mustHospitalInfo = true
                } else if (fieldFlag === reservationFieldUsage.necessary) {
                    rule.medicalDoc.required = true
                }
            }
        } else if (projectMst[ruleKeyName] !== reservationFieldUsage.noUse) {
            rule[fieldKey].show = true
            if (projectMst[ruleKeyName] === reservationFieldUsage.necessary) {
                rule[fieldKey].required = true
            }
        }
        if (isolationFlag) {
            rule.diagonosis.show = false
        }
    });

    Object.keys(fieldOrderRuleMap).forEach((key) => {
        const fieldKey = key as keyof FieldsType<string>;
        const ruleKeyName = fieldOrderRuleMap[fieldKey] as keyof GetProjectDetailDto;

        rule[fieldKey].order = projectMst[ruleKeyName] as (number | null)
    });

    return rule;
}

/**
 * 事業マスタ＞予約時利用項目設定にてお子様別に登録するべき項目が利用されているか否かをリターンする
 * 
 * @param projectMst 
 * @param isolationFlag 
 * @returns 
 */
export const isAnyByChildFormFieldUsed = (projectMst: GetProjectDetailDto, isolationFlag: boolean) => {
    for (const keyName of Object.values(fieldShowRuleMap)) {
        const value = projectMst[keyName as keyof GetProjectDetailDto];

        if (keyName === fieldShowRuleMap.medicalDoc) {
            if (value !== medicalDocUsage.noUse) {
                return true;
            }
            continue;
        }

        // 隔離部屋設定がされている場合は病名は既にカレンダー画面で選択されている
        if (isolationFlag && keyName === fieldShowRuleMap.diagonosis) {
            continue;
        }

        if (value !== reservationFieldUsage.noUse) {
            return true;
        }
    }
    return false;
}
